import { msg } from "../../locales/translate.ts";
import Decimal from "decimal.js";
import { parseStatementTransactions, type StatementTransaction } from "./statementTransactionData.ts";

export const STATEMENT_TRANSACTIONS_EXPORT_PAGE_SIZE = 1000;

const headers = [
	"id", "created_at", "kind", "type", "status", "settlement_status", "settlement_date",
	"mid", "amount", "currency", "fees_amount", "rolling_reserve_amount", "net",
	"reserve_release_date", "channel_id", "channel_alias", "instrument", "instrument_type",
	"instrument_name", "instrument_country", "payment_id", "email", "refund_status", "fail_reason",
] as const satisfies readonly (keyof StatementTransaction)[];

const quote = (value: string) => /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
const textCell = (value: unknown) => {
	const text = String(value ?? "");
	return quote(/^\s*[=+@-]/.test(text) || /^[\t\r\n]/.test(text) ? `'${text}` : text);
};
const amountCell = (value: unknown, minorUnits: boolean) => {
	if (value === null || value === undefined || value === "") return "";
	if ((typeof value !== "number" || !Number.isFinite(value)) && (typeof value !== "string" || !/^-?\d+(?:\.\d+)?$/.test(value))) {
		throw new Error(msg("aTransactionContainsAnInvalidAmountCsvWasNotDownloaded"));
	}
	return minorUnits ? new Decimal(value as string | number).dividedBy(100).toString() : String(value);
};

export const buildStatementTransactionsCsv = (rows: StatementTransaction[]) => {
	const lines = [headers.join(",")];
	for (const row of rows) {
		lines.push(headers.map(header => {
			if (header === "amount" || header === "fees_amount" || header === "rolling_reserve_amount" || header === "net") {
				return amountCell(row[header], header === "amount");
			}
			return textCell(header === "kind" ? row.kind || (row._is_release ? "release" : "txn") : row[header]);
		}).join(","));
	}
	return "\uFEFF" + lines.join("\r\n") + "\r\n";
};

// This endpoint uses zero-based page numbers, not cursors or a download flag.
// Only return a complete, validated statement; failures must never save a partial CSV.
export async function loadStatementTransactionsForCsv(
	loadPage: (page: number) => Promise<unknown>,
	signal: AbortSignal,
	onProgress: (loaded: number, total: number) => void = () => {},
) {
	const rows: StatementTransaction[] = [];
	const seen = new Set<string>();
	let total: number | undefined;
	let perPage: number | undefined;
	for (let page = 0; ; page++) {
		signal.throwIfAborted();
		const data = await loadPage(page);
		signal.throwIfAborted();
		const chunk = parseStatementTransactions(data);
		const pagination = chunk.pagination;
		if (!pagination || pagination.page !== page || !Number.isSafeInteger(pagination.total) || !Number.isSafeInteger(pagination.perPage)) {
			throw new Error(msg("invalidStatementPaginationCsvWasNotDownloaded"));
		}
		if (total !== undefined && (pagination.total !== total || pagination.perPage !== perPage)) {
			throw new Error(msg("theStatementChangedDuringExportPleaseTryAgain"));
		}
		total = pagination.total;
		perPage = pagination.perPage;
		if (chunk.rows.length !== Math.min(perPage, total - rows.length)) {
			throw new Error(msg("couldNotLoadAllStatementTransactionsPleaseTryAgain"));
		}
		for (const row of chunk.rows) {
			// A session may participate both as a transaction and as a reserve release.
			const key = `${row.kind || (row._is_release ? "release" : "txn")}:${row.id}`;
			if (seen.has(key)) throw new Error(msg("theServerRepeatedATransactionPleaseRestartTheExport"));
			seen.add(key);
			rows.push(row);
		}
		onProgress(rows.length, total);
		signal.throwIfAborted();
		if (rows.length === total) return rows;
	}
}
